import { Text } from "@certn/cascadia";
import { GridItem } from "@certn/cascadia-react";
import { css } from "@emotion/css";
import { useTranslation } from "react-i18next";

import { InvoiceProps } from "../../../../src/schemas-and-types";

interface PaidInfoProps {
  invoiceData: InvoiceProps["invoiceData"];
}

function formatPaymentDate(dateStr: string): string | null {
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) {
    return null;
  }
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone: "UTC",
  });
}

type PaymentApplication = NonNullable<
  InvoiceProps["invoiceData"]["payment_applications"]
>[number];

interface PaymentDescription {
  id: string;
  text: string;
}

function getPaymentDescription(
  application: PaymentApplication,
  t: (key: string, options?: Record<string, string>) => string
): PaymentDescription | null {
  const date =
    (application.paid_at && formatPaymentDate(application.paid_at)) ||
    (application.created && formatPaymentDate(application.created)) ||
    null;

  if (!date) {
    return null;
  }

  let text: string;

  if (application.payment_method_detail) {
    text = t("Paid on {{date}} with {{method}}", {
      date,
      method: application.payment_method_detail,
    });
  } else if (application.payment_method_type) {
    const methodLabels: Record<string, string> = {
      CREDIT_CARD: t("credit card"),
      BANK_TRANSFER: t("EFT"),
      CHECK: t("cheque"),
    };
    const label =
      methodLabels[application.payment_method_type] ||
      application.payment_method_type;
    text = t("Paid on {{date}} via {{method}}", { date, method: label });
  } else {
    text = t("Paid on {{date}}", { date });
  }

  return { id: application.id, text };
}

export function PaidInfo({ invoiceData }: PaidInfoProps) {
  const { t } = useTranslation();
  const applications = invoiceData.payment_applications;

  if (!applications || applications.length === 0) {
    return null;
  }

  const descriptions = applications
    .map((app) => getPaymentDescription(app, t))
    .filter((d): d is PaymentDescription => d !== null);

  if (descriptions.length === 0) {
    return null;
  }

  return (
    <GridItem span={12} className={styles.container}>
      <Text style="global/heading-medium">{t("Payment Information")}</Text>
      {descriptions.map(({ id, text }) => (
        <Text key={id} style="global/body-primary">
          {text}
        </Text>
      ))}
    </GridItem>
  );
}

const styles = {
  container: css({
    pageBreakInside: "avoid",
  }),
};
